import {
  createStyles,
  ListItemText,
  makeStyles,
  Paper,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { SearchOutlined } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { matchSorter } from 'match-sorter'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  fuzzySearchMultipleWords,
  markTextSplitBySpaces,
} from 'utils/textFilterUtils'
import { MaterialSummaryDto } from '../../../services/APIs/InternalAPI/internal-api.contracts'
import './AvailableArticles.sass'

export type AvailableArticlesProps = {
  materials: MaterialSummaryDto[]
  selectedMaterial?: MaterialSummaryDto
  onMaterialSelected: (
    material: MaterialSummaryDto,
    shouldSave?: boolean
  ) => void
  width: string
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    optionRoot: {
      '& mark': {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
        // padding: theme.spacing(0, 0.5),
      },
    },
  })
)

const filterOptions = (
  options: Array<MaterialSummaryDto>,
  currentFilter: string
) =>
  fuzzySearchMultipleWords(
    options,
    [
      { key: 'id', minRanking: matchSorter.rankings.CONTAINS },
      { key: 'description', threshold: matchSorter.rankings.CONTAINS },
    ],
    currentFilter
  )

// const debouncedSearch = _.debounce(filterOptions, 500)

export const AvailableArticles = (props: AvailableArticlesProps) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const [showMark, setShowMark] = React.useState(false)

  const [currentFilter, setCurrentFilter] = React.useState('')

  return (
    <div className="available-materials" style={{ width: props.width }}>
      {props.materials?.length > 0 ? (
        <Autocomplete
          options={props.materials}
          noOptionsText={t('project:article-selector--no-available-articles')}
          clearOnEscape
          disablePortal
          freeSolo
          autoHighlight
          ListboxProps={{
            style: { maxHeight: '50vh' },
          }}
          autoSelect
          handleHomeEndKeys
          PaperComponent={(props) => <Paper {...props} elevation={0} />}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={() => currentFilter} // `${option.id} - ${option.description}`
          value={props.selectedMaterial}
          onChange={(event, newValue) => {
            if (!newValue) {
              setShowMark(false)
              return
            }

            if (typeof newValue === 'string') {
              setShowMark(true)
              setCurrentFilter(newValue)
            } else {
              props.onMaterialSelected(newValue as MaterialSummaryDto)
              setShowMark(false)
              setCurrentFilter('')
            }
          }}
          filterOptions={(options) => {
            return filterOptions(options, currentFilter)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchOutlined />,
              }}
              value={currentFilter}
              placeholder="available articles"
              onChange={(event) => {
                setShowMark(true)
                setCurrentFilter(event.target.value)
              }}
            />
          )}
          renderOption={(option: MaterialSummaryDto, state) => (
            <ListItemText
              className={classes.optionRoot}
              primary={
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: showMark
                      ? markTextSplitBySpaces(
                          option.description,
                          state.inputValue
                        )
                      : option.description,
                  }}
                />
              }
              secondary={
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: showMark
                      ? markTextSplitBySpaces(option.id, state.inputValue)
                      : option.id,
                  }}
                  variant="body2"
                  color="textSecondary"
                />
              }
            />
          )}
          open={true}
        />
      ) : (
        <NoAvailableMaterials />
      )}
    </div>
  )
}

const NoAvailableMaterials = () => {
  return (
    <div className="no-available-materials-container">
      <LocalizedTypography
        translationKey={'project:article-selector--no-available-articles'}
      >
        no articles found with the given keywords
      </LocalizedTypography>
    </div>
  )
}
